import { Stack } from "@mui/material"
import { ReactElement, useContext, useEffect, useState } from "react"

import { GRID_SPACING } from "../../../../themes/defaultThemeParams"
import AppStepper from "../../../dataDisplay/stepper/appStepper"
import AppPaper from "../../../surfaces/appPaper"
import { ApplicationContext } from "../applicationProvider"
import DraftSaveIndicator from "../draftSaveIndicator/draftSaveIndicator"
import FormDispatcher from "../formGenerator/formDispatcher"
import Review from "../review/review"

/**
 * Orchestrates the rendering of the application form. Houses the stepper,
 * and the active form.
 *
 * The key prop on the FormGenerator component is used to force a re-render
 * of the form when the user clicks next. This is required to clear the form
 */
const ApplicationOrchestrator = (): ReactElement => {
    const {
        stepLabels,
        applicationForms,
        currentStepIndex,
        setCurrentStepIndex,
        isLastStep,
        formTitlesIndexMap,
        displayStepTitles,
        draftId,
        formData,
        applicationStatus,
    } = useContext(ApplicationContext)
    const [firstLoad, setFirstLoad] = useState<boolean>(true)

    // Move status check logic to useEffect so it only runs when applicationStatus changes
    useEffect(() => {
        if (applicationStatus && firstLoad) {
            if (applicationStatus === "Failed - Invalid Cut-Out Image") {
                setCurrentStepIndex(stepLabels.indexOf("cutOutImages"))
                setFirstLoad(false)
            }
            if (applicationStatus === "Failed - Invalid Site Image") {
                setCurrentStepIndex(stepLabels.indexOf("siteImages"))
                setFirstLoad(false)
            }
        }
    }, [applicationStatus, stepLabels, setCurrentStepIndex])

    return (
        <div data-testid="app-orchestrator">
            <Stack spacing={GRID_SPACING}>
                <AppPaper>
                    <AppStepper
                        stepLabels={displayStepTitles}
                        currentStepIndex={currentStepIndex}
                    />
                </AppPaper>

                {draftId && <DraftSaveIndicator showPermanent />}

                <AppPaper>
                    {isLastStep ? (
                        <Review />
                    ) : (
                        <FormDispatcher
                            key={stepLabels[currentStepIndex]}
                            formKey={stepLabels[currentStepIndex]}
                            displayTitle={
                                formTitlesIndexMap[stepLabels[currentStepIndex]]
                                    .title
                            }
                            displaySubtitle={
                                formTitlesIndexMap[stepLabels[currentStepIndex]]
                                    .subtitle
                            }
                            formProperties={
                                applicationForms[stepLabels[currentStepIndex]]
                            }
                        />
                    )}
                </AppPaper>
            </Stack>
        </div>
    )
}

export default ApplicationOrchestrator
